import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearEntityChatAttachmentAction } from 'store/teamChat/actions';
import { selectPendingEntityChatAttachment } from 'store/teamChat/selectors';

import { sanitizeEntityLabel } from '../../../TeamChat.entityReferences.utils';

// "Discuss in chat" (DEV-5448) seeds the composer draft from OUTSIDE the drawer's subtree (a
// Financial Info / Borrowers row action) via this queued Redux intent rather than DOM manipulation —
// the composer is the sole owner of its draft state (useChatDraft), so it must be the one to apply
// it. `requestId` dedupes so the SAME pending attachment is never applied twice (e.g. re-renders,
// or a chat that briefly remounts), while still allowing a second click of the same entity to re-seed.
//
// The dispatched label is a HINT: on surfaces where the financialInfo slice isn't loaded at click
// time (the broker portal never loads it outside this drawer — DEV-5697's bare-`#` seed), it arrives
// empty. A label-less intent WAITS for the candidates fetch this composer already triggers, then
// resolves the label from the candidate; an entity that no longer exists is dropped rather than
// seeding junk.
const useChatEntityAttachment = ({ chatId, onSeed, candidatesByType, isCandidatesLoaded }) => {
  const dispatch = useDispatch();
  const pending = useSelector(selectPendingEntityChatAttachment);
  const appliedRequestIdRef = useRef(null);

  useEffect(() => {
    if (
      !pending ||
      pending.chatId !== chatId ||
      appliedRequestIdRef.current === pending.requestId
    ) {
      return;
    }

    const hintLabel = sanitizeEntityLabel(pending.label ?? '');

    if (!hintLabel && !isCandidatesLoaded) {
      return;
    }

    const label =
      hintLabel ||
      candidatesByType?.[pending.entityType]?.find(
        candidate => candidate.entityId === pending.entityId,
      )?.label ||
      '';

    appliedRequestIdRef.current = pending.requestId;

    if (label) {
      onSeed({ entityType: pending.entityType, entityId: pending.entityId, label });
    }

    dispatch(clearEntityChatAttachmentAction());
  }, [pending, chatId, onSeed, dispatch, candidatesByType, isCandidatesLoaded]);
};

export default useChatEntityAttachment;
